import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, ArrowRight, Building2, Check, Copy, KeyRound, Loader2, Mail, ShieldCheck, Smartphone, Users,
} from "lucide-react";
import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import { API } from "../apiBase";

const detail = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);
const MDP_OK = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);

/** Activation de l'espace MetaConnect d'un demandeur OhMyVue : mot de passe puis double authentification. */
export default function MssanteActivation() {
  const { setSession } = useProAuth();
  const navigate = useNavigate();
  const [jeton] = useState(() => new URLSearchParams(window.location.search).get("t") || "");
  const [inv, setInv] = useState(null);
  const [erreurLien, setErreurLien] = useState("");
  const [etape, setEtape] = useState("compte");
  const [mdp, setMdp] = useState("");
  const [mdp2, setMdp2] = useState("");
  const [cgu, setCgu] = useState(false);
  const [rgpd, setRgpd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [secours, setSecours] = useState([]);
  const [copie, setCopie] = useState(false);

  useEffect(() => {
    if (!jeton) { setErreurLien("Lien d'activation incomplet : ouvrez le lien reçu par e-mail."); return; }
    axios.get(`${API}/mss-espace/invitation`, { params: { t: jeton } })
      .then(({ data }) => setInv(data))
      .catch((e) => setErreurLien(detail(e, "Ce lien d'activation n'est pas valable.")));
    try { window.history.replaceState(null, "", window.location.pathname); } catch (e) { /* jeton retiré de la barre d'adresse */ }
  }, [jeton]);

  const activer = async (ev) => {
    ev?.preventDefault?.();
    setErr("");
    if (!inv.compte_existant) {
      if (!MDP_OK(mdp)) return setErr("Le mot de passe doit comporter au moins 8 caractères, dont une lettre et un chiffre.");
      if (mdp !== mdp2) return setErr("Les deux mots de passe ne correspondent pas.");
      if (!cgu || !rgpd) return setErr("Acceptez les CGU et le traitement de vos données pour continuer.");
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/mss-espace/activer`, {
        t: jeton, mot_de_passe: mdp, accept_cgu: cgu, accept_data: rgpd,
      });
      if (data.compte_existant) { setEtape("rattache"); return; }
      setSession(data.token, data.user);
      const r = await axios.post(`${API}/pro/2fa/setup`);
      setQr(r.data);
      setEtape("2fa");
    } catch (e) {
      setErr(detail(e, "Activation impossible pour le moment. Réessayez dans quelques minutes."));
    } finally { setBusy(false); }
  };

  const activer2fa = async (ev) => {
    ev?.preventDefault?.();
    setErr(""); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pro/2fa/enable`, { code: code.replace(/\s/g, "") });
      setSecours(data.recovery_codes || []);
      setEtape("secours");
    } catch (e) {
      setErr(detail(e, "Code invalide : saisissez le code à 6 chiffres affiché par votre application."));
    } finally { setBusy(false); }
  };

  const copier = async () => {
    try { await navigator.clipboard.writeText(secours.join("\n")); setCopie(true); } catch (e) { /* copie refusée */ }
  };

  const Etapes = () => (
    <ol className="flex items-center gap-2 text-[11.5px] text-[#64748B] mb-6" aria-label="Étapes">
      {[["compte", "Mot de passe"], ["2fa", "Double authentification"], ["secours", "Codes de secours"]].map(([k, l], i) => {
        const ordre = ["compte", "2fa", "secours"];
        const fait = ordre.indexOf(etape) > i;
        const actif = etape === k;
        return (
          <li key={k} className="flex items-center gap-2">
            <span className={`grid place-items-center h-6 w-6 rounded-full border text-[11px] ${fait ? "bg-emerald-600 border-emerald-600 text-white" : actif ? "border-[#0F2B5B] text-[#0F2B5B]" : "border-slate-300"}`}>
              {fait ? <Check size={12} /> : i + 1}
            </span>
            <span className={actif ? "text-[#0F2B5B] font-medium" : ""}>{l}</span>
            {i < 2 && <span className="w-6 h-px bg-slate-300" />}
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className="min-h-screen bg-[#F4F7FB]">
      <SEO title="Activer mon espace · MetaConnect MSSanté" canonical="/mssante/activation" noIndex />
      <section className="pt-32 pb-20 lg:pt-36">
        <div className="max-w-xl mx-auto px-5">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-700">
            <ShieldCheck size={13} /> Espace sécurisé MetaConnect
          </div>
          <h1 className="mt-3 font-display text-[#0F2B5B] text-3xl font-semibold leading-tight text-balance">
            {etape === "rattache" ? "Boîte ajoutée à votre espace" : "Activez votre espace"}
          </h1>

          {erreurLien && (
            <div className="mt-6 rounded-xl border border-red-200 bg-white p-5 text-[14px] text-[#334155]" role="alert">
              <p className="flex gap-2 text-red-700"><AlertCircle size={16} className="shrink-0 mt-0.5" />{erreurLien}</p>
              <p className="mt-3">Vous avez déjà activé votre espace ? <Link to="/mssante/connexion" className="text-[#0369A1] underline">Connectez-vous</Link>.</p>
            </div>
          )}

          {!erreurLien && !inv && (
            <p className="mt-6 inline-flex items-center gap-2 text-[14px] text-[#475569]"><Loader2 size={15} className="animate-spin" /> Chargement de votre invitation…</p>
          )}

          {inv && (
            <>
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 grid gap-2 text-[13.5px] text-[#334155]" data-testid="activation-resume">
                <div className="flex items-center gap-2"><Mail size={14} className="text-[#0369A1]" /><span className="font-mono text-[#0F2B5B]">{inv.adresse}</span></div>
                <div className="flex items-center gap-2"><Building2 size={14} className="text-[#0369A1]" />{inv.etablissement}</div>
                <div className="flex items-center gap-2"><Users size={14} className="text-[#0369A1]" />{inv.nb_utilisateurs} utilisateur{inv.nb_utilisateurs > 1 ? "s" : ""} déjà déclaré{inv.nb_utilisateurs > 1 ? "s" : ""} · demande {inv.reference}</div>
              </div>

              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6">
                {etape !== "rattache" && !inv.compte_existant && <Etapes />}

                {etape === "compte" && inv.compte_existant && (
                  <div className="grid gap-4">
                    <p className="text-[14px] text-[#334155]">Un espace MetaConnect existe déjà pour <strong>{inv.email}</strong>. Nous y ajoutons cette boîte, sa structure et ses utilisateurs.</p>
                    {err && <p className="text-[13px] text-red-700" role="alert">{err}</p>}
                    <button type="button" onClick={activer} disabled={busy} data-testid="activation-rattacher"
                      className="btn-primary justify-self-start px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
                      {busy && <Loader2 size={14} className="animate-spin" />} Ajouter la boîte à mon espace
                    </button>
                  </div>
                )}

                {etape === "compte" && !inv.compte_existant && (
                  <form onSubmit={activer} className="grid gap-4" noValidate>
                    <p className="text-[14px] text-[#334155]">Bonjour {inv.prenom}, choisissez le mot de passe de votre espace. Identifiant : <strong className="font-mono">{inv.email}</strong></p>
                    <input type="email" autoComplete="username" value={inv.email} readOnly hidden />
                    <label className="grid gap-1.5 text-[12px] uppercase tracking-[0.14em] text-[#0369A1]" htmlFor="act-mdp">Mot de passe
                      <input id="act-mdp" type="password" autoComplete="new-password" className="mc-input normal-case tracking-normal" value={mdp}
                        onChange={(e) => setMdp(e.target.value)} data-testid="activation-mdp" />
                      <span className={`normal-case tracking-normal text-[12px] ${mdp && !MDP_OK(mdp) ? "text-amber-700" : "text-[#64748B]"}`}>8 caractères minimum, dont une lettre et un chiffre.</span>
                    </label>
                    <label className="grid gap-1.5 text-[12px] uppercase tracking-[0.14em] text-[#0369A1]" htmlFor="act-mdp2">Confirmer le mot de passe
                      <input id="act-mdp2" type="password" autoComplete="new-password" className="mc-input normal-case tracking-normal" value={mdp2}
                        onChange={(e) => setMdp2(e.target.value)} data-testid="activation-mdp2" />
                    </label>
                    <label className="flex items-start gap-2.5 text-[13px] text-[#334155]">
                      <input id="act-cgu" type="checkbox" className="mt-0.5 h-4 w-4" checked={cgu} onChange={(e) => setCgu(e.target.checked)} data-testid="activation-cgu" />
                      <span>J'accepte les <Link to="/cgu" target="_blank" className="text-[#0284C7] underline">CGU</Link> et les <Link to="/cgv" target="_blank" className="text-[#0284C7] underline">CGV</Link> de MetaConnect.</span>
                    </label>
                    <label className="flex items-start gap-2.5 text-[13px] text-[#334155]">
                      <input id="act-rgpd" type="checkbox" className="mt-0.5 h-4 w-4" checked={rgpd} onChange={(e) => setRgpd(e.target.checked)} data-testid="activation-rgpd" />
                      <span>Je consens au traitement de mes données conformément à la <Link to="/donnees" target="_blank" className="text-[#0284C7] underline">charte RGPD</Link>.</span>
                    </label>
                    {err && <p className="text-[13px] text-red-700" role="alert">{err}</p>}
                    <button type="submit" disabled={busy} data-testid="activation-submit"
                      className="btn-primary justify-self-start px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Créer mon espace
                    </button>
                  </form>
                )}

                {etape === "2fa" && qr && (
                  <form onSubmit={activer2fa} className="grid gap-4">
                    <p className="text-[14px] text-[#334155]"><strong>Dernière étape : la double authentification.</strong> Scannez ce QR code avec une application d'authentification (Google Authenticator, Microsoft Authenticator, FreeOTP…), puis saisissez le code affiché.</p>
                    <div className="flex items-center gap-5 flex-wrap">
                      <img src={qr.qr_data_uri} alt="QR code à scanner avec votre application d'authentification" className="h-44 w-44 rounded-lg border border-slate-200" />
                      <div className="text-[12.5px] text-[#475569] grid gap-1 min-w-0">
                        <span className="inline-flex items-center gap-1.5"><Smartphone size={13} /> Saisie manuelle :</span>
                        <code className="font-mono text-[12px] break-all bg-slate-50 rounded px-2 py-1 text-[#0F2B5B]">{qr.secret}</code>
                      </div>
                    </div>
                    <label className="grid gap-1.5 text-[12px] uppercase tracking-[0.14em] text-[#0369A1]" htmlFor="act-code">Code à 6 chiffres
                      <input id="act-code" inputMode="numeric" autoComplete="one-time-code" maxLength={8}
                        className="mc-input normal-case tracking-[0.3em] font-mono w-44" value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="activation-code" />
                    </label>
                    {err && <p className="text-[13px] text-red-700" role="alert">{err}</p>}
                    <button type="submit" disabled={busy || code.length < 6} data-testid="activation-2fa"
                      className="btn-primary justify-self-start px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Activer la double authentification
                    </button>
                  </form>
                )}

                {etape === "secours" && (
                  <div className="grid gap-4">
                    <p className="text-[14px] text-[#334155]"><strong>Votre espace est protégé.</strong> Conservez ces codes de secours en lieu sûr : chacun permet une connexion si vous perdez votre téléphone.</p>
                    <ul className="grid grid-cols-2 gap-1.5 font-mono text-[13px] bg-slate-50 rounded-lg p-4 text-[#0F2B5B]" data-testid="activation-secours">
                      {secours.map((c) => <li key={c}>{c}</li>)}
                    </ul>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={copier} className="btn-ghost px-4 py-2 rounded-full text-[12.5px] inline-flex items-center gap-1.5">
                        {copie ? <Check size={13} /> : <Copy size={13} />} {copie ? "Codes copiés" : "Copier les codes"}
                      </button>
                      <button type="button" onClick={() => navigate("/mssante/espace")} data-testid="activation-espace"
                        className="btn-primary px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2">
                        Accéder à mon espace <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {etape === "rattache" && (
                  <div className="grid gap-4">
                    <p className="text-[14px] text-[#334155]">La boîte <strong className="font-mono">{inv.adresse}</strong>, sa structure et ses utilisateurs sont maintenant dans votre espace.</p>
                    <p className="text-[13px] text-[#475569]">Pour protéger vos échanges, activez la double authentification depuis la page « Sécurité » de votre espace si ce n'est pas déjà fait.</p>
                    <Link to="/mssante/connexion" className="btn-primary justify-self-start px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2" data-testid="activation-connexion">
                      Me connecter <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
